import {Injectable} from "@angular/core";
import {RestolutionService} from "./restolution";
import {BestoranteService} from "./bestorante";
import {Platform} from "@ionic/angular";
import { StorageService } from "./storage.service";
import {BestoGoConfigurationDto} from "../types/ca";
import {UtilsService} from "./utils";
import {Plugins} from "@capacitor/core";
import {EventsService} from "./events.service";
import { Logger, LoggingService } from "ionic-logging-service";
import { ReceiptStore } from "./receiptstore";
// eslint-disable-next-line @typescript-eslint/naming-convention
const {App} = Plugins;

@Injectable()
export class ConfigurationService {

  public username;
  public password;
  public platform;
  public paymentTerminal;
  public poplatekTerminalNr;
  public confData: BestoGoConfigurationDto;
  public guestMode = false;
  private poplatekDevEnv = false;
  private appVersion = "-";
  private machineNumber;

  private logger: Logger;

  constructor(private resto: RestolutionService, private besto: BestoranteService,
    private storage: StorageService, private platformService: Platform, private utils: UtilsService,
              private events: EventsService, private loggingService: LoggingService,
              private receiptStore: ReceiptStore) {
      this.logger = loggingService.getLogger("BestoGo.Configuration");
      if (App) {
        App.getInfo().then((info) => {
          if (info) {
            this.appVersion = info.version + " b" + info.build;
            if (!this.confData) {
              this.initConf().then(() => {
                this.confData.appVersion = this.appVersion;
              });
            } else {
              this.confData.appVersion = this.appVersion;
            }
          }
        });
      }
      this.events.getObservable().subscribe((msg) => {
        if (msg) {
          if (msg.type === "PopupRegResponse") {
           this.logger.info("Got registration response", msg);
            if (msg.data && msg.data.machineNumber) {
              this.confData.crMachineNumber = msg.data.machineNumber;
              this.machineNumber = msg.data.machineNumber;
             this.logger.info("Machine number set to " + msg.data.machineNumber);
            }
          }
        }
      });
  }

  async setConfData(confData: any) {
    if (!confData) {
      return;
    }
    if (confData.paymentTerminal) {
      confData.paymentTerminalType = confData.paymentTerminal;
    }
    this.resto.bestoUrl = confData.baseUrl;
    this.resto.restoUuid = confData.uuid;
    this.resto.serverUrl = confData.serverUrl;
    this.resto.crUuid = confData.crUuid;
    this.resto.crId = confData.crId;
    this.resto.msgHeader.fromOu = confData.crOuId;
    this.resto.msgHeader.fromMachineNumber = "GO" + confData.crMachineNumber;
    this.besto.restoUuid = confData.uuid;
    this.besto.bestoUrl = confData.baseUrl;
    this.username = confData.username;
    this.password = confData.password;

    if (confData.orientation) {
      this.storage.set(StorageService.orientationKey, confData.orientation);
    }

    if (confData.paymentTerminalType) {
      if (this.getPlatform() === "ios" && confData.poplatekTerminalNr) {
        confData.paymentTerminalType = "POPLATEKWS";
      } else if (this.getPlatform() === "ios" && !confData.poplatekTerminalNr) {
        confData.paymentTerminalType = "NONE";
      }
    } else if (!confData.paymentTerminalType && this.getPlatform() === "ios") {
      confData.paymentTerminalType = "NONE";
    } else {
      confData.paymentTerminalType = "NETS_SMARTPOS";
    }
    this.paymentTerminal = confData.paymentTerminalType;
    this.logger.info("setConfData", "Set payment terminal to " + this.paymentTerminal);

    if (confData.poplatekTerminalNr) {
      this.poplatekTerminalNr = confData.poplatekTerminalNr;
    }

    if (confData.poplatekDevEnv) {
      this.poplatekDevEnv = true;
    } else {
      this.poplatekDevEnv = false;
    }

    this.confData = confData;
    this.storage.set("storedConfData", confData);
  }

  async initConf() {
    let confData = await this.storage.get("storedConfData");
   this.logger.info("initConf", "Got confData", confData);
    if (confData == null) {
      confData = {
        crId: 0,
        crMachineNumber: "",
        crOuId: 0,
        crUuid: "",
        environment: undefined,
        fromQRCode: false,
        goUuid: this.utils.generateGUIDString(),
        language: "",
        orientation: "",
        password: "",
        paymentTerminalType: "NETS_SMARTPOS",
        poplatekDevEnv: false,
        poplatekKey: "",
        poplatekTerminalNr: "",
        username: "",
        uuid: "",
        serverUrl: "https://web.restolution.fi/api/",
        baseUrl: "https://bestorante.com",
        appVersion: this.appVersion
      };
    }
    confData.appVersion = this.appVersion;
    if (!confData.crMachineNumber) {
      confData.crMachineNumber = this.machineNumber;
      this.logger.debug("initConf", "Setting machine number" + confData.crMachineNumber);
    } else {
      this.logger.debug("initConf", "Confdata machine number " + confData.crMachineNumber);
    }
    await this.setConfData(confData);
  }

  public getPlatform(): string {
    if (!this.platform) {
      this.platform =
        (this.platformService.is("ios") || this.platformService.is("ipad") || this.platformService.is("iphone")) ?
         "ios" :
         "android";
    }
    return this.platform;
  }

  public isNetsPt() {
    return (this.getPlatform() === "android" && this.paymentTerminal === "NETS_SMARTPOS");
  }

  public isPoplatekWsPt() {
    return (this.paymentTerminal === "POPLATEKWS");
  }

  public getPoplatekTerminalNr() {
    return this.poplatekTerminalNr;
  }

  public isPoplatekDevEnv() {
    return this.poplatekDevEnv;
  }

  public getGoUuid() {
    return this.confData.goUuid;
  }

  public getOuId() {
    return this.confData.crOuId;
  }

  public getMachineNumber() {
    return this.confData.crMachineNumber;
  }

  public getUsername() {
    return this.username;
  }

  public clearClientSpecificData() {
    this.confData.crOuId = null;
    this.confData.crUuid = null;
    this.confData.uuid = null;
    this.confData.password = null;
    this.confData.poplatekTerminalNr = null;
    this.confData.crMachineNumber = null;
    this.storage.set("clerkID", null);
    this.storage.set("receiptNumber", 1);
    this.storage.set("sentReceipts", []);
    this.storage.set("unsentReceipts", []);
    this.storage.set("reconciliatedDates", []);
    this.storage.set("discounts", []);
  }

  public getAppVersion() {
    return this.appVersion;
  }

  public getCurrency() {
    if (this.besto && this.besto.restaurant && this.besto.restaurant.currency) {
      return this.besto.restaurant.currency;
    }
    /* this.logger.warn("getCurrency", "Returning default currency EUR"); */
    return "EUR";
  }
}
